import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, finalize, map, switchMap } from 'rxjs/operators';
import { Like } from '@modules/common/types/like';
import { Section } from '@modules/common/types/section';
import { DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { DragNDropService } from '@modules/drag-n-drop/services/drag-n-drop.service';
import { NestedDataItem } from '@modules/drag-n-drop/types/nested-item';
import { Column } from '@modules/tasks/types/column';
import { Project } from '@modules/tasks/types/project';
import { Task } from '@modules/tasks/types/task';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { applyTaskListFilters, isManualOrder } from '@modules/tasks/utils/task-list-filters.util';
import { newTaskDropPredicate, seedFromDroppedEntity } from '@modules/tasks/utils/new-task-drop.util';
import { BoardService } from '@modules/tasks/services/board.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { TaskQuickFormComponent } from '@modules/elements/components/quick-forms/task-quick-form/task-quick-form.component';
import { ModalService } from '@modules/modal/services/modal.service';

@Component({
  selector: 'app-project-tasks-list-column',
  templateUrl: './project-tasks-list-column.component.html',
  host: { class: 'block' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectTasksListColumnComponent {
  private static readonly SECTION_LABEL_OFFSET = 2;

  public readonly project = input<Project>();
  public readonly section = input<Section>();
  public readonly column = input.required<Column>();
  public readonly filters = input<Like<TasksFilters>>({});

  public readonly clickTask = output<Task>();
  public readonly openTask = output<Task>();
  public readonly doubleClickTask = output<Task>();
  public readonly openSubtask = output<Task>();

  protected readonly tasksChanges = new Subject<NestedDataItem<Task>[]>();
  protected readonly dndPredicate = (dragData: DragData): boolean => dragData.type === DragDataTypes.task;

  private readonly boardService = inject(BoardService);
  private readonly dndService = inject(DragNDropService);
  private readonly tasksService = inject(TasksService);
  private createInProgress = false;

  private readonly modalService = inject(ModalService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly quickTask = signal<Task | null>(null);
  protected readonly newTaskDndPredicate = newTaskDropPredicate;
  protected readonly canAddTask = computed(() => {
    const section = this.section();
    return !!this.column().title && (!section || !!section.title);
  });

  private readonly columnId = computed(() => this.column().id);
  // Rebuild the form only when the column identity changes, not on every column()
  // emission (reorder/upsert re-creates Column objects) — otherwise an in-progress
  // rename would be discarded mid-edit.
  protected readonly columnForm = computed(() => {
    this.columnId();
    return untracked(() => this.column().asFormGroup());
  });
  protected readonly dndEnabled = computed(() => isManualOrder(this.filters()));

  protected readonly sectionPositionLabel = computed(() => {
    const section = this.section();
    return section ? section.position + ProjectTasksListColumnComponent.SECTION_LABEL_OFFSET : undefined;
  });

  private readonly rawTasks = toSignal(
    combineLatest([toObservable(this.column), toObservable(this.section)]).pipe(
      distinctUntilChanged(([c1, s1], [c2, s2]) => c1?.id === c2?.id && s1?.id === s2?.id),
      switchMap(([column, section]) => this.boardService.getTasks(column, section)),
    ),
    { initialValue: [] },
  );

  protected readonly tasks = computed(() => applyTaskListFilters(this.rawTasks(), this.filters()));
  protected readonly count = computed(() => this.tasks().length);

  protected readonly collapsed = toSignal(
    toObservable(this.columnId).pipe(switchMap((columnId) => this.boardService.getColumnCollapsed(columnId))),
    { initialValue: false },
  );

  protected readonly dragIds = toSignal(this.dndService.getDragIds(), { initialValue: [] });
  protected readonly draggedIdSet = computed(() => new Set(this.dragIds()));
  private readonly draggedType = toSignal(this.dndService.getDraggingDataChanges().pipe(map((data) => data?.type)));
  protected readonly dragging = computed(() => this.draggedType() === DragDataTypes.task);

  constructor() {
    this.tasksChanges.pipe(takeUntilDestroyed()).subscribe((changes) => {
      this.boardService.updateTasks(changes, this.section()?.id, this.column().id);
      this.expandOnDrop();
    });
  }

  protected handleBlur(): void {
    const titleControl = this.columnForm().controls.title;
    if (!titleControl.dirty || (titleControl.value ?? '').trim() === '') {
      return;
    }

    this.boardService.updateColumn(Column.fromFormGroup(this.columnForm()));
  }

  protected toggleCollapse(): void {
    this.boardService.setColumnCollapsed(this.column().id, !this.collapsed());
  }

  protected handleNewTask(seed: Partial<Task> = {}): void {
    this.quickTask.set(
      new Task({
        ...seed,
        accountId: this.project()?.accountId,
        projectId: this.column().projectId,
        sectionId: this.section()?.id,
        columnId: this.column().id,
      }),
    );
  }

  /** Dropping an entity on "Add a new task" opens the quick form pre-filled from it (SBUX-897). */
  protected handleNewTaskDrop(dragData: DragData): void {
    this.handleNewTask(seedFromDroppedEntity(dragData));
  }

  protected handleQuickFormClose(): void {
    this.quickTask.set(null);
  }

  protected handleQuickFormSave(task: Task, quickForm?: TaskQuickFormComponent): void {
    if (this.createInProgress) return;
    this.createInProgress = true;
    const tasks = this.tasks();
    task.columnId = this.column().id;
    task.sectionId = this.section()?.id;
    task.position =
      tasks.reduce((memo, { parentId, position }) => (parentId === null && memo < position ? position : memo), -1) + 1;
    task.boardPosition =
      tasks.reduce(
        (memo, item) => (item.parentId === null && memo < (item.boardPosition ?? 0) ? (item.boardPosition ?? 0) : memo),
        -1,
      ) + 1;

    this.tasksService
      .create(task, this.boardService.itemRequestConfig())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.createInProgress = false;
        }),
      )
      .subscribe({
        next: (savedTask) => {
          this.boardService.addTask(savedTask);
          this.quickTask.set(null);
        },
        error: (error) => quickForm?.handleError(error),
      });
  }

  protected handleQuickFormMore(task: Task): void {
    this.modalService.openFullForm(task, this.injector, this.boardService.itemRequestConfig().requestContext);
  }

  // sorted mode only — manual mode handles drops via *dndNestedFor
  protected handleColumnDrop(dragData: DragData): void {
    this.boardService.moveTasksToColumn(dragData.data, this.section()?.id, this.column().id);
    this.expandOnDrop();
  }

  // Keep the group the task was dropped into visible — otherwise, once the drag
  // ends, a collapsed column/section snaps back to 0fr and hides the dropped task.
  private expandOnDrop(): void {
    if (this.collapsed()) {
      this.boardService.setColumnCollapsed(this.column().id, false);
    }

    const section = this.section();
    if (section) {
      this.boardService.setSectionCollapsed(section.id, false);
    }
  }
}
